import {
  asOptional,
  assertNotEmpty,
  type Compare,
  type Optional,
} from '@festina-life/core'
import { useTranslate } from '@festina-life/ui/hooks'
import type { PropsWithChildren } from 'react'

import { useTemplateContext } from '../../context/templateContext'
import {
  isConfigurationValueSetupField,
  isFieldWithCardinality,
  isSimpleTemplateField,
  isSimpleValueSetupField,
  isSubTemplateValueSetupField,
  isTemplateReferenceValueSetupField,
  type TemplateField,
  type ValueSetupFieldValue,
  ValueSetupRole,
} from '../../types/templateModel'
import { SimpleFieldValueDynamicFunction } from '../dynamicFunction/SimpleFieldValueDynamicFunction'
import { functionDeclaration } from '../templateFieldsTable/functionHelpers'
import { FieldValidationsValueDetails } from '../templateFieldValidations/FieldValidationsValueDetails'
import { ComparableDocumentationDiff } from './ComparableDocumentationDiff'
import { ConfigurationFieldExpressionValueDiff } from './ConfigurationFieldExpressionValueDiff'
import { DiffPopoverLayout } from './DiffPopoverLayout'
import { isSimpleValueSetupFieldValue } from './helpers'
import { ListValuesDiff } from './ListValuesDiff'
import { SimpleTemplateFieldConfigDiff } from './SimpleTemplateFieldConfigDiff'
import { SimpleValueSetupFrameValueDiff } from './SimpleValueSetupFrameValueDiff'
import { TemplateFieldDiffHeader } from './TemplateFieldDiffHeader'
import { TemplateReferenceDiff } from './TemplateReferenceDiff'
import { useCompareContext } from './valueSetupCompareContext'

type Props = Compare<TemplateField> & {
  isShowingFieldName?: boolean
}

export const TemplateFieldDiff = ({
  isShowingFieldName,
  current,
  compare,
  children,
}: PropsWithChildren<Props>) => {
  const { templateVersion, isMappingsTemplate } = useTemplateContext()
  const { shouldCompare } = useCompareContext()
  const translate = useTranslate()

  assertNotEmpty(templateVersion)

  const typedCurrentValue = current?.value as Optional<ValueSetupFieldValue>
  const typedCompareValue = compare?.value as Optional<ValueSetupFieldValue>

  return (
    <DiffPopoverLayout>
      <div className="mb-8 grid grid-cols-2 gap-x-24">
        <TemplateFieldDiffHeader
          fieldName={
            isShowingFieldName ? (current?.name ?? compare?.name) : undefined
          }
          current={current?.origin}
          compare={compare?.origin}
        />
      </div>
      <div className="flex flex-col gap-y-4">
        {shouldCompare('documentation') && (
          <ComparableDocumentationDiff
            current={current?.value?.documentation?.value}
            compare={compare?.value?.documentation?.value}
          />
        )}
        {shouldCompare('valueConfig') && (
          <SimpleTemplateFieldConfigDiff
            current={typedCurrentValue?.valueConfig?.value}
            compare={typedCompareValue?.valueConfig?.value}
          />
        )}
        {shouldCompare('required') && (
          <ConfigurationFieldExpressionValueDiff
            current={typedCurrentValue?.required}
            compare={typedCompareValue?.required}
            title={translate('diffPopover.field.required')}
          />
        )}
        {shouldCompare('requiredInModelInstance') && (
          <ConfigurationFieldExpressionValueDiff
            current={typedCurrentValue?.requiredInModelInstance}
            compare={typedCompareValue?.requiredInModelInstance}
            title={translate('diffPopover.field.requiredInModelInstance')}
          />
        )}
        {asOptional(current, isFieldWithCardinality) &&
          asOptional(compare, isFieldWithCardinality) &&
          shouldCompare('cardinality') && (
            <ConfigurationFieldExpressionValueDiff
              current={current?.value?.cardinality}
              compare={compare?.value?.cardinality}
              title={translate('diffPopover.field.cardinality')}
            />
          )}
        {asOptional(current, isConfigurationValueSetupField) &&
          asOptional(compare, isConfigurationValueSetupField) &&
          shouldCompare('defaultValue') && (
            <ConfigurationFieldExpressionValueDiff
              current={current?.value}
              compare={compare?.value}
              title={translate('diffPopover.field.defaultValue')}
            />
          )}
        {asOptional(current, isSimpleTemplateField) &&
          isSimpleValueSetupFieldValue(current?.value) &&
          asOptional(compare, isSimpleTemplateField) &&
          isSimpleValueSetupFieldValue(compare?.value) && (
            <>
              {shouldCompare('defaultValue') && (
                <ConfigurationFieldExpressionValueDiff
                  current={current?.value?.defaultValue}
                  compare={compare?.value?.defaultValue}
                  title={translate('diffPopover.field.defaultValue')}
                />
              )}
              {shouldCompare('frame') && (
                <SimpleValueSetupFrameValueDiff
                  title={translate('diffPopover.field.frame')}
                  current={current?.value?.frame}
                  compare={compare?.value?.frame}
                />
              )}
            </>
          )}
        {asOptional(current, isTemplateReferenceValueSetupField) &&
          asOptional(compare, isTemplateReferenceValueSetupField) &&
          shouldCompare('variants') && (
            <ListValuesDiff
              title={translate('diffPopover.field.templateReferences')}
              current={current?.value?.variants}
              compare={compare?.value?.variants}
              keyFn={(value) => (isMappingsTemplate ? value.key : value.source)}
              renderValue={(value) => <TemplateReferenceDiff value={value} />}
            />
          )}
        {asOptional(current, isSubTemplateValueSetupField) &&
          asOptional(compare, isSubTemplateValueSetupField) &&
          (compare || current)?.role !==
            ValueSetupRole.SINGLE_CLASS_SUB_TEMPLATE &&
          shouldCompare('variants') && (
            <ListValuesDiff
              title={translate('diffPopover.field.subTemplateVariants')}
              current={current?.value?.variants}
              compare={compare?.value?.variants}
              keyFn={(value) => value.path}
              renderValue={(value) => (
                <div>{value.subTemplateIdentifier ?? ''}</div>
              )}
            />
          )}
        {shouldCompare('validations') && (
          <ListValuesDiff
            title={translate('diffPopover.field.validations')}
            current={typedCurrentValue?.validations}
            compare={typedCompareValue?.validations}
            keyFn={(validation) => validation.key?.expression?.fplusSource}
            renderValue={(value, origin) => (
              <FieldValidationsValueDetails
                validations={[value]}
                fieldPath={
                  (origin === 'current' ? current?.path : compare?.path) ?? ''
                }
                isShowingTitle={false}
              />
            )}
          />
        )}
        {asOptional(current, isSimpleValueSetupField) &&
          asOptional(compare, isSimpleValueSetupField) &&
          shouldCompare('dynamicFunctions') && (
            <ListValuesDiff
              title={translate('diffPopover.field.dynamicFunctions')}
              current={current?.value?.dynamicFunctions}
              compare={compare?.value?.dynamicFunctions}
              keyFn={(dynamicFunction) =>
                functionDeclaration(dynamicFunction.function?.fplusSource)
              }
              renderValue={(value) => (
                <SimpleFieldValueDynamicFunction
                  value={value}
                  isShowingTitle={false}
                  isShowingErrors={false}
                />
              )}
            />
          )}
        {children}
      </div>
    </DiffPopoverLayout>
  )
}
